import React, { useState, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { blogPosts, siteConfig } from '../../data/mock';
import {
  Search,
  Calendar,
  User,
  Tag,
  ArrowLeft,
  ArrowRight,
  Clock,
  BookOpen,
  ChevronRight,
  MessageCircle,
} from 'lucide-react';

// Blog List Page
export const BlogPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState(null);

  // Get all unique tags
  const allTags = useMemo(() => {
    const tags = new Set();
    blogPosts.forEach((post) => post.tags.forEach((tag) => tags.add(tag)));
    return Array.from(tags);
  }, []);

  // Filter posts
  const filteredPosts = useMemo(() => {
    return blogPosts.filter((post) => {
      const matchesSearch =
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesTag = selectedTag ? post.tags.includes(selectedTag) : true;
      return matchesSearch && matchesTag;
    });
  }, [searchTerm, selectedTag]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <main className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto space-y-6">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#329780]/10 border border-[#329780]/20">
              <BookOpen className="w-4 h-4 text-[#329780] mr-2" />
              <span className="text-[#329780] text-sm font-medium">Blog</span>
            </div>

            <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight">
              Conteúdo <span className="text-[#329780]">especializado</span>
            </h1>

            <p className="text-lg text-slate-400 leading-relaxed">
              Artigos, tutoriais e insights sobre tecnologia, segurança e
              automação para manter sua empresa atualizada.
            </p>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-center gap-6">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <Input
                type="text"
                placeholder="Buscar artigos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-[#111920] border-[#1e293b] text-white placeholder:text-slate-500 focus:border-[#329780]"
              />
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedTag(null)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  selectedTag === null
                    ? 'bg-[#329780] text-white'
                    : 'bg-[#111920] text-slate-400 hover:text-white hover:bg-[#1e293b]'
                }`}
              >
                Todos
              </button>
              {allTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => setSelectedTag(tag)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    selectedTag === tag
                      ? 'bg-[#329780] text-white'
                      : 'bg-[#111920] text-slate-400 hover:text-white hover:bg-[#1e293b]'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-slate-400 text-lg">Nenhum artigo encontrado.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPosts.map((post, index) => (
                <Link key={post.id} to={`/blog/${post.slug}`}>
                  <Card className="bg-[#111920] border-[#1e293b] card-hover h-full group">
                    <CardContent className="p-0">
                      {/* Image Placeholder */}
                      <div className="aspect-video bg-[#0a0f14] relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-[#329780]/20 to-transparent" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <BookOpen className="w-12 h-12 text-[#329780]/30" />
                        </div>
                        {/* Tag Badge */}
                        <div className="absolute top-4 left-4">
                          <span className="px-3 py-1 bg-[#329780] text-white text-xs font-medium rounded-full">
                            {post.tags[0]}
                          </span>
                        </div>
                      </div>

                      <div className="p-6 space-y-4">
                        {/* Meta */}
                        <div className="flex items-center space-x-4 text-slate-500 text-sm">
                          <span className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1" />
                            {formatDate(post.date)}
                          </span>
                          <span className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            5 min
                          </span>
                        </div>

                        {/* Title */}
                        <h3 className="text-lg font-semibold text-white group-hover:text-[#329780] transition-colors duration-200 line-clamp-2">
                          {post.title}
                        </h3>

                        {/* Excerpt */}
                        <p className="text-slate-400 text-sm leading-relaxed line-clamp-3">
                          {post.excerpt}
                        </p>

                        {/* Read More */}
                        <div className="flex items-center text-[#329780] text-sm font-medium">
                          Ler artigo
                          <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-200" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

// Blog Post Page
export const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const post = blogPosts.find((p) => p.slug === slug);

  const handleWhatsApp = () => {
    window.open(
      `https://wa.me/${siteConfig.whatsapp}?text=${encodeURIComponent(siteConfig.whatsappMessage)}`,
      '_blank'
    );
  };

  if (!post) {
    return (
      <main className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Artigo não encontrado</h1>
          <Button onClick={() => navigate('/blog')} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar ao Blog
          </Button>
        </div>
      </main>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  // Related posts
  const relatedPosts = blogPosts
    .filter((p) => p.id !== post.id && p.tags.some((t) => post.tags.includes(t)))
    .slice(0, 3);

  return (
    <main className="min-h-screen pt-20">
      {/* Article Header */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Link */}
          <Link
            to="/blog"
            className="inline-flex items-center text-slate-400 hover:text-[#329780] transition-colors duration-200 mb-8"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar ao Blog
          </Link>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-6">
            {post.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-[#329780]/10 text-[#329780] text-sm font-medium rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
            {post.title}
          </h1>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-6 text-slate-400 text-sm">
            <span className="flex items-center">
              <User className="w-4 h-4 mr-2" />
              {post.author}
            </span>
            <span className="flex items-center">
              <Calendar className="w-4 h-4 mr-2" />
              {formatDate(post.date)}
            </span>
            <span className="flex items-center">
              <Clock className="w-4 h-4 mr-2" />
              5 min de leitura
            </span>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Featured Image Placeholder */}
          <div className="aspect-video bg-[#111920] rounded-2xl mb-12 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#329780]/20 to-transparent" />
            <div className="absolute inset-0 flex items-center justify-center">
              <BookOpen className="w-20 h-20 text-[#329780]/30" />
            </div>
          </div>

          {/* Content */}
          <article className="prose prose-invert prose-lg max-w-none">
            {post.content.split('\n\n').map((paragraph, index) => {
              if (paragraph.startsWith('## ')) {
                return (
                  <h2 key={index} className="text-2xl font-bold text-white mt-10 mb-4">
                    {paragraph.replace('## ', '')}
                  </h2>
                );
              }
              if (paragraph.startsWith('### ')) {
                return (
                  <h3 key={index} className="text-xl font-semibold text-white mt-8 mb-3">
                    {paragraph.replace('### ', '')}
                  </h3>
                );
              }
              if (paragraph.startsWith('- ') || paragraph.includes('\n- ')) {
                const items = paragraph.split('\n').filter((item) => item.startsWith('- '));
                return (
                  <ul key={index} className="space-y-2 my-4">
                    {items.map((item, i) => (
                      <li key={i} className="text-slate-300">
                        {item.replace('- ', '')}
                      </li>
                    ))}
                  </ul>
                );
              }
              if (paragraph.match(/^\d\./)) {
                const items = paragraph.split('\n').filter((item) => item.match(/^\d\./) || item.startsWith('**'));
                return (
                  <ol key={index} className="space-y-2 my-4 list-decimal list-inside">
                    {items.map((item, i) => (
                      <li key={i} className="text-slate-300">
                        {item.replace(/^\d\.\s?/, '').replace(/\*\*/g, '')}
                      </li>
                    ))}
                  </ol>
                );
              }
              return (
                <p key={index} className="text-slate-300 leading-relaxed my-4">
                  {paragraph}
                </p>
              );
            })}
          </article>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#080c10]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#111920] border border-[#1e293b] rounded-2xl p-8 sm:p-12 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">
              Precisa de ajuda com este assunto?
            </h2>
            <p className="text-slate-400 mb-6">
              Nossa equipe está pronta para ajudar sua empresa a implementar as
              melhores soluções.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contato">
                <Button
                  size="lg"
                  className="bg-[#329780] hover:bg-[#287a68] text-white px-8"
                >
                  Fale Conosco
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Button
                variant="outline"
                size="lg"
                onClick={handleWhatsApp}
                className="border-slate-700 text-slate-300 hover:bg-white/5 px-8"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Artigos relacionados</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <Link key={relatedPost.id} to={`/blog/${relatedPost.slug}`}>
                  <Card className="bg-[#111920] border-[#1e293b] card-hover h-full group">
                    <CardContent className="p-6 space-y-4">
                      <span className="px-3 py-1 bg-[#329780]/10 text-[#329780] text-xs font-medium rounded-full">
                        {relatedPost.tags[0]}
                      </span>
                      <h3 className="text-lg font-semibold text-white group-hover:text-[#329780] transition-colors duration-200 line-clamp-2">
                        {relatedPost.title}
                      </h3>
                      <p className="text-slate-400 text-sm line-clamp-2">
                        {relatedPost.excerpt}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
};
